import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';
import { useAuth } from '../../constants/providers/AuthProvider';

export interface FeedPost {
  id: string;
  user_id: string;
  content: string;
  media_urls: { type: 'image' | 'video'; url: string }[] | null;
  created_at: string;
  updated_at: string;
  is_pinned: boolean;
  location_id: string | null;
  company_id: string | null;
  is_company_wide: boolean;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  location_name: string | null;
  reactions: { type: string; count: number }[] | null;
  comments_count: number;
}

export interface FeedComment {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export function useFeed() {
  const { data: companyId } = useCurrentCompany();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['feed', companyId],
    queryFn: async (): Promise<FeedPost[]> => {
      const { data, error } = await (supabase
        .from('posts_feed_optimized' as never)
        .select('*')
        .eq('visible', true)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .range(0, 29) as unknown as Promise<{ data: FeedPost[] | null; error: unknown }>);

      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    posts: (query.data || []) as FeedPost[],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    userId,
    queryClient,
  };
}

/** Fetch comments for a single post */
export function usePostComments(postId: string | null) {
  const query = useQuery({
    queryKey: ['post-comments', postId],
    queryFn: async (): Promise<FeedComment[]> => {
      if (!postId) return [];

      // Fetch comments
      const { data: comments, error } = await supabase
        .from('comments')
        .select('id, content, user_id, created_at')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (!comments || comments.length === 0) return [];

      // Batch-fetch user profiles for commenters
      const userIds = [...new Set(comments.map((c) => c.user_id).filter((id): id is string => !!id))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, avatar_url')
        .in('id', userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.id, p])
      );

      return comments.map((c) => {
        const profile = c.user_id ? profileMap.get(c.user_id) : undefined;
        return {
          id: c.id,
          content: c.content ?? '',
          user_id: c.user_id ?? '',
          created_at: c.created_at ?? '',
          first_name: profile?.first_name ?? null,
          last_name: profile?.last_name ?? null,
          avatar_url: profile?.avatar_url ?? null,
        };
      });
    },
    enabled: !!postId,
    staleTime: 30 * 1000,
  });

  return {
    comments: (query.data || []) as FeedComment[],
    isLoading: query.isLoading,
  };
}

/** Add a comment to a post */
export function useAddComment() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, content }: { postId: string; content: string }) => {
      if (!userId) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('comments')
        .insert({ post_id: postId, user_id: userId, content });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['post-comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}

/** Toggle a reaction on a post */
export function useToggleReaction() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, reactionType }: { postId: string; reactionType: string }) => {
      if (!userId) throw new Error('Not authenticated');

      // Check if user already reacted with this type
      const { data: existing } = await supabase
        .from('reactions')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .eq('reaction_type', reactionType)
        .limit(1);

      if (existing && existing.length > 0) {
        // Remove reaction
        await supabase.from('reactions').delete().eq('id', existing[0].id);
      } else {
        // Add reaction
        await supabase
          .from('reactions')
          .insert({ post_id: postId, user_id: userId, reaction_type: reactionType });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}
